import type { ReactNode } from "react";

export type ToastId = string;

export type ToastVariant = "success" | "danger" | "warning" | "info";

/** Время показа в мс; `null` — без автозакрытия (только кнопкой или `toast.dismiss(id)`). */
export type ToastDuration = number | null;

export const TOAST_DEFAULT_DURATION_MS = 5000;

export interface ToastRenderContext {
    id: ToastId;
    /** Закрыть этот элемент (с анимацией). */
    dismiss: () => void;
}

/**
 * Что показывает элемент.
 * `message` — стандартный `Toast`, `custom` — любой компонент (например, будущий `ToastUpload`).
 */
export type ToastContent =
    | { type: "message"; variant: ToastVariant; title?: ReactNode; message: ReactNode }
    | { type: "custom"; render: (context: ToastRenderContext) => ReactNode };

export interface ToastEntry {
    id: ToastId;
    content: ToastContent;
    duration: ToastDuration;
    /** `closing` — элемент проигрывает анимацию исчезновения и будет удалён `remove`. */
    status: "open" | "closing";
    /** Увеличивается при каждом обновлении элемента; новое значение перезапускает таймер. */
    version: number;
}

export interface ToastOptions {
    /** Свой id. Если элемент с таким id уже есть, он обновляется, а не дублируется. */
    id?: ToastId;
    /** По умолчанию `TOAST_DEFAULT_DURATION_MS`. */
    duration?: ToastDuration;
}

type Listener = () => void;

const EMPTY: readonly ToastEntry[] = [];

export function createToastStore() {
    let entries: readonly ToastEntry[] = EMPTY;
    let lastId = 0;
    const listeners = new Set<Listener>();

    const setEntries = (next: readonly ToastEntry[]) => {
        entries = next;
        listeners.forEach((listener) => listener());
    };

    return {
        subscribe(listener: Listener): () => void {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },

        getSnapshot(): readonly ToastEntry[] {
            return entries;
        },

        getServerSnapshot(): readonly ToastEntry[] {
            return EMPTY;
        },

        /** Добавляет элемент или обновляет существующий с тем же id. Возвращает id. */
        upsert(content: ToastContent, { id, duration = TOAST_DEFAULT_DURATION_MS }: ToastOptions = {}): ToastId {
            const existing = id === undefined ? undefined : entries.find((entry) => entry.id === id);

            if (existing) {
                const updated: ToastEntry = {
                    ...existing,
                    content,
                    duration,
                    status: "open",
                    version: existing.version + 1,
                };
                setEntries(entries.map((entry) => (entry.id === existing.id ? updated : entry)));
                return existing.id;
            }

            const newId = id ?? `toast-${++lastId}`;
            setEntries([...entries, { id: newId, content, duration, status: "open", version: 0 }]);
            return newId;
        },

        /** Запускает закрытие элемента; без id — всех элементов. */
        dismiss(id?: ToastId): void {
            if (!entries.some((entry) => entry.status === "open" && (id === undefined || entry.id === id))) {
                return;
            }
            setEntries(
                entries.map((entry) =>
                    entry.status === "open" && (id === undefined || entry.id === id)
                        ? { ...entry, status: "closing" }
                        : entry,
                ),
            );
        },

        /** Удаляет элемент сразу, без анимации. */
        remove(id: ToastId): void {
            if (entries.some((entry) => entry.id === id)) {
                setEntries(entries.filter((entry) => entry.id !== id));
            }
        },

        /** Элемент показан или ждёт очереди и не закрывается. */
        isActive(id: ToastId): boolean {
            return entries.some((entry) => entry.id === id && entry.status === "open");
        },
    };
}

export type ToastStore = ReturnType<typeof createToastStore>;
